import { StrictMode, useMemo, type ReactNode } from 'react';
import { createRoot } from 'react-dom/client';
import { ThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { QueryClientProvider } from '@tanstack/react-query';
import { getThemeFor } from './theme/rittecTheme';
import { AuthProvider } from './context/AuthContext';
import { PermissionProvider } from './context/PermissionContext';
import { ThemeModeProvider, useThemeMode } from './context/ThemeContext';
import { SocketProvider } from './context/SocketContext';
import { NotificationProvider } from './context/NotificationContext';
import { HelpModeProvider } from './context/HelpModeContext';
import { ErrorBoundary } from './components/common/ErrorBoundary';
import { BugReportProvider } from './components/bug-report/BugReportProvider';
import { App } from './App';
import './styles/pm-tokens.css';
import './styles/themes.css';
import './i18n';
import { logger } from './lib/logger';
import { registerGlobalErrorHandlers } from './lib/globalErrorHandler';
import { initDiagnose } from './lib/diagnose';
import { installNetworkCapture } from './lib/networkBuffer';
import { queryClient } from './api/queryClient';

// Müssen VOR dem ersten Render laufen — sonst sind die Diagnose-Puffer des
// Melde-Werkzeugs (bug-report) beim ersten Fehler/Request bereits leer.
registerGlobalErrorHandlers();
installNetworkCapture();
initDiagnose();

logger.info('boot', `PM Frontend gestartet`, {
  // __APP_VERSION__ kommt per vite `define` aus package.json. VITE_APP_VERSION
  // wurde hier gelesen, aber nirgends gesetzt — die Zeile meldete deshalb auf
  // jeder Umgebung 'dev', ausgerechnet dort, wo man den laufenden Stand sucht.
  version: __APP_VERSION__,
  mode: import.meta.env.MODE,
  userAgent: navigator.userAgent,
  language: navigator.language,
  viewport: `${window.innerWidth}x${window.innerHeight}`,
  url: window.location.href,
});

/** Waehlt das MUI-Theme passend zum gewaehlten Theme-Namen aus dem ThemeContext. */
function ThemedRoot({ children }: { children: ReactNode }) {
  const { theme } = useThemeMode();
  const muiTheme = useMemo(() => getThemeFor(theme), [theme]);
  return (
    <ThemeProvider theme={muiTheme}>
      <CssBaseline />
      {children}
    </ThemeProvider>
  );
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <ThemeModeProvider>
      <ThemedRoot>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <PermissionProvider>
            <SocketProvider>
              <NotificationProvider>
                <HelpModeProvider>
                  <BugReportProvider>
                    <ErrorBoundary>
                      <App />
                    </ErrorBoundary>
                  </BugReportProvider>
                </HelpModeProvider>
              </NotificationProvider>
            </SocketProvider>
            </PermissionProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ThemedRoot>
    </ThemeModeProvider>
  </StrictMode>,
);
